import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { doc, getDoc, deleteDoc } from 'firebase/firestore'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  CardMedia,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
} from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { useSnackbar } from '../contexts/SnackbarContext'
import { db } from '../firebase'
import { useAuthWatcher } from '../hooks/useAuthWatcher'
import { Shop } from '../type/common'
import noImage from '../assets/no-img.jpg'

type ShopData = Shop & { id: string }

type LocationState = {
  fromPage?: number
  selectedTags?: string[]
}

export const ShopDetailPage = () => {
  const { shopId } = useParams<{ shopId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const locationState = location.state as LocationState
  const fromPage = locationState?.fromPage || 1
  const fromTags = locationState?.selectedTags || []

  const { user, isLoading: authLoading } = useAuthWatcher()
  const { showSnackbar } = useSnackbar()
  const [shop, setShop] = useState<ShopData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  useEffect(() => {
    const fetchShop = async () => {
      if (!shopId) {
        navigate('/')
        return
      }

      if (!user?.uid) {
        return
      }

      try {
        const docRef = doc(db, 'shops', shopId)
        const docSnap = await getDoc(docRef)

        if (docSnap.exists()) {
          const data = docSnap.data() as Shop

          // ユーザーの所有する店舗かチェック
          if (data.userId !== user?.uid) {
            navigate('/')
            return
          }
          setShop({
            ...data,
            id: docSnap.id,
          })
        } else {
          navigate('/')
        }
      } catch (error) {
        showSnackbar('店舗情報の取得に失敗しました', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    if (!authLoading && user !== undefined) {
      fetchShop()
    }
  }, [shopId, navigate, user?.uid, user, authLoading])

  const handleDelete = async () => {
    if (!shopId) return

    try {
      await deleteDoc(doc(db, 'shops', shopId))
      showSnackbar('店舗を削除しました', 'success')
      setTimeout(() => {
        // 削除後はページ番号を保持してHomeに戻る
        navigate('/', { state: { returnPage: fromPage } })
      }, 1000)
    } catch (error) {
      showSnackbar('削除に失敗しました', 'error')
    }
    setDeleteDialogOpen(false)
  }

  const handleEdit = () => {
    // 編集画面にもページ番号を引き継ぐ
    navigate(`/edit-shop/${shopId}`, {
      state: {
        fromPage,
        selectedTags: fromTags,
      },
    })
  }

  // 戻るボタンのハンドラー
  const handleBack = () => {
    navigate('/', {
      state: {
        returnPage: fromPage,
        selectedTags: fromTags,
      },
    })
  }

  const isSafeUrl = (url: string): boolean => {
    try {
      const parsedUrl = new URL(url)
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
    } catch {
      return false
    }
  }

  const shouldRenderAsLink = (text: string): boolean => {
    const isUrl = /^https?:\/\//.test(text)
    return isUrl && isSafeUrl(text)
  }

  const renderAddress = (address: string) => {
    if (shouldRenderAsLink(address)) {
      return (
        <Link
          href={address}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          color="primary.main"
          sx={{
            wordBreak: 'break-all',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {address}
        </Link>
      )
    }
    return (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          wordBreak: 'break-word',
        }}
      >
        {address}
      </Typography>
    )
  }

  if (authLoading || isLoading) {
    return (
      <Box maxWidth="407px" m="auto" p={2}>
        <Box height="50vh" display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      </Box>
    )
  }

  if (!shop) {
    return null
  }

  return (
    <Box maxWidth="400px" m="auto" py={2}>
      {/* ヘッダー */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} px={2}>
        <Button
          variant="text"
          startIcon={<ArrowBackIosNewIcon />}
          onClick={handleBack}
          sx={{ marginBottom: '8px' }}
        >
          戻る
        </Button>
        <Box display="flex" gap={2}>
          <IconButton
            onClick={handleEdit}
            color="primary"
            size="small"
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => setDeleteDialogOpen(true)}
            color="error"
            size="small"
            sx={{
              backgroundColor: 'error.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'error.dark',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* メインコンテンツ */}
      <Box>
        <Box
          sx={{
            width: '100%',
            height: 'auto',
            aspectRatio: '4/5',
            position: 'relative',
            mx: 'auto',
          }}
        >
          {!imageLoaded && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <CardMedia
            component="img"
            image={shop.imageUrl || noImage}
            alt={shop.name}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageLoaded(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />

          {/* グラデーションオーバーレイ */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '35%',
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 20%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.4) 100%)',
              pointerEvents: 'none',
            }}
          />

          {/* 画像上のテキストコンテンツ */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              color: 'white',
            }}
          >
            <Typography
              variant="h6"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              }}
            >
              {shop.name}
            </Typography>

            {shop.tags && (
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {(() => {
                  const tagsArray = Array.isArray(shop.tags)
                    ? shop.tags
                    : shop.tags
                        .split('、')
                        .filter((tag) => tag.trim() !== '')
                        .map((tag) => tag.trim())

                  return tagsArray.map((tag: string, index: number) => (
                    <Chip key={index} label={`${tag}`} color="info" size="small" />
                  ))
                })()}
              </Box>
            )}
          </Box>
        </Box>

        <CardContent sx={{ px: 2, py: 3 }}>
          {/* メモ */}
          {shop.description && (
            <Box mb={3}>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.7,
                }}
              >
                {shop.description}
              </Typography>
            </Box>
          )}

          {/* 住所 */}
          {shop.address && (
            <Box display="flex" alignItems="center" mb={2}>
              <LocationOnIcon color="action" sx={{ mr: 1 }} />
              {renderAddress(shop.address)}
            </Box>
          )}

          {/* 作成日 */}
          {shop.createdAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'right' }}>
              作成日: {shop.createdAt.toDate().toLocaleDateString('ja-JP')}
            </Typography>
          )}
        </CardContent>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">店舗を削除しますか？</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            「{shop.name}」を削除します。この操作は取り消せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            キャンセル
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" autoFocus>
            削除
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}
